import React, { useState, useEffect, useContext } from "react";
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import { SECONDORY_COLOR } from "../common/sharedFunctions";
import { ThemeProvider } from "@mui/material/styles";
import theme from "styles/tableStyle";
import TableStyle from "../components/Table/Style";
import localization from "../components/Table/Localization";
import { FirebaseContext, api } from "common";

export default function CustomerDetailReport() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((state) => state.settingsdata.settings);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { bookingRef, settingsRef } = useContext(FirebaseContext);
  
  function formatAmount(value, decimal, country) {
    const number = parseFloat(value || 0);
    if (country === "Vietnam") {
      return number.toLocaleString("vi-VN", {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
      });
    } else {
      return number.toLocaleString("en-US", {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
      });
    }
  }

  const columns = [
    { title: t("year"), field: "year" },
    { title: t("months"), field: "monthsName" },
    { title: t("customer_name"), field: "customerName" },
    { title: t("booking_count"), field: "total_rides" },
    { title: t("Total Kilometers"), field: "totalKilometers" },
    { title: t("Cash"), field: "cash", 
    render: row => formatAmount(row.cash, settings.decimal, settings.country)
    },
    { title: t("Wallet"), field: "wallet", 
      render: row => formatAmount(row.wallet, settings.decimal, settings.country)
    },
    { title: t("Card"), field: "card", 
      render: row => formatAmount(row.card, settings.decimal, settings.country)
    },
    {
      title: t("Total Spent"),
      field: "totalSpent",
      render: (rowData) =>
        rowData.totalSpent
          ? formatAmount(
              rowData.totalSpent,
              settings.decimal,
              settings.country
            )
          : 0,
    },
  ];

  useEffect(() => {
    if (!bookingRef || !settingsRef) return;
    
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        const firebaseDatabase = await import("firebase/database");
        const { get, onValue } = firebaseDatabase;
        
        const settingsdata = await get(settingsRef);
        const settings = settingsdata.val();
        // console.log("Total bookings found:", settings);
        
        onValue(bookingRef, (snapshot) => {
          if (snapshot.val()) {
            const mainArr = snapshot.val();
            // console.log("Total bookings found:",mainArr);
            
            const monthsName = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];
            
            // Create a map to store customer data by year_month_customer
            const customerMonthData = {};
            
            Object.keys(mainArr).forEach((j) => {
              const booking = mainArr[j];
              
              // Only include PAID or COMPLETE bookings with driver_share defined
              if (booking.status === 'PAID' || booking.status === 'COMPLETE') {
                // console.log("Bookings",booking)
                const bdt = new Date(booking.tripdate);
                const tripDistance = booking.estimateDistance || booking.distance || 0;
            // console.log("Total bookings found:",booking);
                
                // Get customer name from various possible fields
                let customerName = "Unknown Customer";
                if (booking.customer_name) {
                  customerName = booking.customer_name;
                } else if (booking.user_name) {
                  customerName = booking.user_name;
                } else if (booking.userDetails && booking.userDetails.firstName) {
                  customerName = `${booking.userDetails.firstName} ${booking.userDetails.lastName || ''}`.trim();
                } else if (booking.firstName) {
                  customerName = `${booking.firstName} ${booking.lastName || ''}`.trim();
                }
                
                // Use customer name as the unique identifier since user ID is undefined
                const customerId = booking.user || customerName;
                
                // Create unique key for year_month_customer
                const uniqueKey = `${bdt.getFullYear()}_${bdt.getMonth()}_${customerId}`;
                
                // console.log("Processing booking:", j, "Customer:", customerName, "Month:", bdt.getMonth(), "Year:", bdt.getFullYear(), "CustomerId:", customerId, "UniqueKey:", uniqueKey);
                
                if (customerMonthData[uniqueKey]) {
                  // Add to existing customer data for this month
            // console.log("Total bookings found:",booking);

                  customerMonthData[uniqueKey].totalSpent += parseFloat(booking.trip_cost || 0);
                  customerMonthData[uniqueKey].total_rides += 1;
                  customerMonthData[uniqueKey].totalKilometers += parseFloat(tripDistance);

            // 💰 Add payment mode calculations
                  const pm = booking.payment_mode || "Unknown";

                  if (pm === "cash") customerMonthData[uniqueKey].cash += booking.trip_cost || 0;
                  if (pm === "wallet") customerMonthData[uniqueKey].wallet += booking.trip_cost || 0;
                  if (pm === "card") customerMonthData[uniqueKey].card += booking.trip_cost || 0;
                } else {
                  // Create new entry for this customer in this month
                  customerMonthData[uniqueKey] = {
                    year: bdt.getFullYear(),
                    month: bdt.getMonth(),
                    monthsName: monthsName[bdt.getMonth()],
                    customerName: customerName,
                    customerId: customerId,
                    totalSpent: parseFloat(booking.trip_cost || 0),
                    total_rides: 1,
                    totalKilometers: parseFloat(tripDistance),
                    
                    // 💰 NEW FIELDS
                    cash: booking.payment_mode === "cash" ? parseFloat(booking.trip_cost || 0) : 0,
                    wallet: booking.payment_mode === "wallet" ? parseFloat(booking.trip_cost || 0) : 0,
                    card: booking.payment_mode === "card" ? parseFloat(booking.trip_cost || 0) : 0, 

                    uniqueKey: uniqueKey
                  };
                }
              }
            });
            
            // console.log("Customer month data:", customerMonthData);
            
            // Convert to array and sort
            const result = Object.keys(customerMonthData).map(key => {
              const item = customerMonthData[key];
              return {
                ...item,
                totalSpent: item.totalSpent.toFixed(settings.decimal),
                totalKilometers: item.totalKilometers.toFixed(settings.decimal),
               
                // NEW formatted fields
                cash: item.cash.toFixed(settings.decimal),
                wallet: item.wallet.toFixed(settings.decimal),
                card: item.card.toFixed(settings.decimal),
              };
            });
            
            // Sort by year, then month, then customer name
            result.sort((a, b) => {
              if (a.year !== b.year) return b.year - a.year; // Newest year first
              if (a.month !== b.month) return b.month - a.month; // Newest month first
              return a.customerName.localeCompare(b.customerName); // Alphabetical by name
            });
            
            // console.log("Final result:", result);
            setData(result);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error("Error fetching customer data:", error);
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [bookingRef, settingsRef]);

  const [selectedRow, setSelectedRow] = useState(null);

  return loading ? (
    <CircularLoading />
  ) : (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title={t("CUSTOMER DETAILS")}
        columns={columns}
        style={{
          direction: isRTL === "rtl" ? "rtl" : "ltr",
          borderRadius: "8px",
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: "20px",
        }}
        data={data}
        onRowClick={(evt, selectedRow) =>
          setSelectedRow(selectedRow.tableData.id)
        }
        options={{
          exportButton: true,
          rowStyle: (rowData) => ({
            backgroundColor:
              selectedRow === rowData.tableData.id
                ? colors.ROW_SELECTED
                : colors.WHITE,
          }),
          ...TableStyle(),
        }}
        localization={localization(t)}
      />
    </ThemeProvider>
  );
}
